import {createContext, useContext} from 'react'
import {ParsedUrlQuery} from 'querystring'
import {UrlObject} from 'url'

import {logger} from '../../../utils/logging/logger'

const INITIAL_VALUE = {} as RouterContextProps

export type Url = UrlObject | string

export type QueryParams = ParsedUrlQuery | {[key: string]: unknown}

export interface TransitionOptions {
  shallow?: boolean
}

export interface RouterContextProps {
  /**
   * A function that pushes a new URL to the history stack
   * @param url - The URL to push
   */
  push: (url: Url, options?: TransitionOptions) => void | Promise<boolean>
  /**
   * A function that replaces the current URL in the history stack
   * @param url - The URL to replace
   */
  replace: (url: Url, options?: TransitionOptions) => void | Promise<boolean>
  /**
   * A function that navigates back in the history stack
   */
  goBack: () => void
  /**
   * A function that returns the query parameters of the current URL
   * @returns The query parameters
   */
  getQueryParams: () => QueryParams
  /**
   * The current URL query parameter string (URL search string)
   */
  queryString: string
  /**
   * The current URL pathname, without the query string
   */
  pathname: string
}

/**
 * This context is used to provide the router context to the app.
 * It is used to navigate the app and get the query parameters of the current URL.
 *
 * This is a temporary context to facilitate the migration to Next.js.
 * It should be deleted as we fully migrate the app to the framework.
 */
export const RouterContext = createContext<RouterContextProps>(INITIAL_VALUE)

/**
 * Provides router functions & properties that work within react-router and next/router context.
 * Use this for any shared code.
 * @returns The router context value
 * @example
 * ```
 * const {push, replace, goBack, getQueryParams, pathname, queryString} = useRouterProvider()
 * ```
 */
export const useRouterProvider = () => {
  const value = useContext(RouterContext)
  if (value === INITIAL_VALUE) {
    logger.error(
      'useRouterProvider',
      new Error('Must be used within a RouterProvider component')
    )
  }
  return value
}
